/*
 * video-hero.js
 * Custom element <video-hero> that powers sections/video-hero.liquid.
 *
 * Responsibilities:
 *   - Show one slide at a time (crossfade), with a numbered "circle" switcher.
 *   - Lazy-load ONLY the active slide's video, and only the correct orientation
 *     (horizontal on >=750px, vertical below) via a JS source swap. Never fetch
 *     both files on one device.
 *   - Autoplay muted/looping inline; progress ring fills over `duration`, then
 *     auto-advances. Click a circle to jump.
 *   - Pause the video + timer when the hero scrolls off-screen or the tab is
 *     hidden; resume when it returns.
 *   - Respect prefers-reduced-motion: posters only, no video load, no autoplay.
 *   - If a video fails to load, fall back to its poster.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 750px)';
  var REDUCED_MQ = '(prefers-reduced-motion: reduce)';

  class VideoHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.video-hero__slide')
      );
      this.dots = Array.prototype.slice.call(
        this.querySelectorAll('.video-hero__dot')
      );

      // Cache each slide's <video> and wire its playing/failed state here so
      // the element is self-sufficient on Theme Editor re-renders.
      this.slides.forEach(function (slide) {
        var video = slide.querySelector('.video-hero__video');
        slide._video = video || null;
        if (!video) return;
        video.addEventListener('playing', function () {
          video.classList.add('is-playing');
        });
        video.addEventListener('error', function () {
          video.classList.add('is-failed');
          video.classList.remove('is-playing');
        });
      });

      this.desktopMedia = window.matchMedia(DESKTOP_MQ);
      this.reducedMedia = window.matchMedia(REDUCED_MQ);

      // Section-level default; each slide may override it via data-duration.
      this.sectionDuration = (parseFloat(this.dataset.duration) || 6) * 1000;
      this.duration = this.sectionDuration;
      this.autoAdvance = this.dataset.autoAdvance !== 'false' && this.slides.length > 1;

      this.index = 0;
      this.rafId = null;
      this.lastTick = 0;
      this.elapsed = 0;
      this.running = false;
      this.inView = true;

      // Pre-compute ring geometry so JS can drive the progress offset.
      this.dots.forEach(function (dot) {
        var ring = dot.querySelector('.video-hero__ring-progress');
        if (!ring) return;
        var r = parseFloat(ring.getAttribute('r')) || 0;
        var circ = 2 * Math.PI * r;
        ring.style.strokeDasharray = circ;
        ring.style.strokeDashoffset = circ;
        ring._circ = circ;
      });

      // Wire the switcher circles.
      var self = this;
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          self.goTo(i, true);
        });
      });

      // React to breakpoint changes (rotate a phone, resize a window):
      // swap the active video to the matching orientation, debounced.
      this._onBreakpoint = debounce(this.handleBreakpointChange.bind(this), 250);
      addMediaListener(this.desktopMedia, this._onBreakpoint);
      window.addEventListener('resize', this._onBreakpoint);
      window.addEventListener('orientationchange', this._onBreakpoint);
      this._lastDesktop = this.desktopMedia.matches;

      // Measure the header/announcement-bar height above the hero so a
      // full-height hero fits under it (keeps the switcher on-screen).
      this.setHeaderOffset();
      this._onResize = debounce(this.setHeaderOffset.bind(this), 200);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('orientationchange', this._onResize);
      // Re-measure after fonts/images settle (header height can change).
      window.addEventListener('load', this._onResize);

      // Pause when the tab is hidden.
      this._onVisibility = this.handleVisibility.bind(this);
      document.addEventListener('visibilitychange', this._onVisibility);

      // React to a live reduced-motion preference change.
      addMediaListener(this.reducedMedia, this.handleReducedChange.bind(this));

      // Pause when the hero scrolls out of view; resume when it returns.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          self.inView = entries[0].isIntersecting;
          if (self.inView) {
            self.resume();
          } else {
            self.pause();
          }
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }

      // Activate the first slide (eager). Slides 2..n stay unloaded until shown.
      this.activate(0, false);
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this._onBreakpoint);
      window.removeEventListener('orientationchange', this._onBreakpoint);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._onResize);
      window.removeEventListener('load', this._onResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    get reduced() {
      return this.reducedMedia.matches;
    }

    /* Which orientation source should the active device use right now? */
    sourceFor(slide) {
      var desktop = slide._video ? slide._video.getAttribute('data-desktop') : '';
      var mobile = slide._video ? slide._video.getAttribute('data-mobile') : '';
      if (this.desktopMedia.matches) {
        return { url: desktop || mobile, kind: 'desktop' };
      }
      // Mobile: fall back to the desktop file (center-cropped via object-fit)
      // if no vertical video was provided, so the section never breaks.
      return { url: mobile || desktop, kind: mobile ? 'mobile' : 'desktop' };
    }

    /* Lazily attach the correct src to a slide's <video> and play it. */
    loadAndPlay(slide) {
      if (this.reduced) return; // posters only
      var video = slide._video;
      if (!video) return;

      var src = this.sourceFor(slide);
      if (!src.url) return; // no video set -> poster stays

      if (video._loadedUrl !== src.url) {
        video._loadedUrl = src.url;
        video.classList.remove('is-failed', 'is-playing');
        video.src = src.url;
        video.load();
      }

      video.muted = true; // required for autoplay on iOS/Android
      video.playsInline = true;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* Autoplay blocked -> leave the poster; not fatal. */
        });
      }
    }

    pauseVideo(slide) {
      var video = slide && slide._video;
      if (video && !video.paused) {
        try { video.pause(); } catch (e) {}
      }
    }

    /* Switch the visible slide. */
    activate(i, userInitiated) {
      if (i < 0 || i >= this.slides.length) return;
      this.index = i;

      var self = this;
      this.slides.forEach(function (slide, idx) {
        var active = idx === i;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          self.loadAndPlay(slide);
        } else {
          self.pauseVideo(slide);
        }
      });

      this.dots.forEach(function (dot, idx) {
        var active = idx === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        var ring = dot.querySelector('.video-hero__ring-progress');
        if (ring) ring.style.strokeDashoffset = ring._circ; // reset ring
      });

      // Use this slide's own duration if set (data-duration > 0), else the
      // section default.
      var slideDur = parseFloat(this.slides[i].dataset.duration);
      this.duration = (slideDur && slideDur > 0) ? slideDur * 1000 : this.sectionDuration;

      // Restart the progress timer for this slide.
      this.elapsed = 0;
      if (userInitiated || !this.reduced) {
        this.restartTimer();
      }
    }

    /* Public-ish: go to a specific slide (used by clicks). */
    goTo(i, userInitiated) {
      this.activate(i, userInitiated);
    }

    next() {
      this.activate((this.index + 1) % this.slides.length, false);
    }

    /* ---- Progress-ring timer (rAF so pause/resume is exact) ------------ */
    restartTimer() {
      this.elapsed = 0;
      this.startLoop();
    }

    /* Start (or continue, from current `elapsed`) the ring/advance loop. */
    startLoop() {
      this.stopTimer();
      if (!this.autoAdvance || this.reduced || !this.inView || document.hidden) return;
      this.running = true;
      this.lastTick = now();
      var self = this;
      var loop = function () {
        if (!self.running) return;
        var t = now();
        self.elapsed += t - self.lastTick;
        self.lastTick = t;

        var progress = Math.min(self.elapsed / self.duration, 1);
        self.updateRing(progress);

        if (progress >= 1) {
          self.running = false;
          self.next();
          return;
        }
        self.rafId = window.requestAnimationFrame(loop);
      };
      this.rafId = window.requestAnimationFrame(loop);
    }

    stopTimer() {
      this.running = false;
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    updateRing(progress) {
      var dot = this.dots[this.index];
      if (!dot) return;
      var ring = dot.querySelector('.video-hero__ring-progress');
      if (!ring) return;
      ring.style.strokeDashoffset = ring._circ * (1 - progress);
    }

    /* ---- Pause / resume ------------------------------------------------ */
    pause() {
      this.stopTimer();
      this.pauseVideo(this.slides[this.index]);
    }

    resume() {
      if (this.reduced) return;
      this.loadAndPlay(this.slides[this.index]);
      // Continue the ring from where it left off (don't reset elapsed).
      this.startLoop();
    }

    handleVisibility() {
      if (document.hidden) {
        this.pause();
      } else if (this.inView) {
        this.resume();
      }
    }

    /* Space above the hero (announcement bar + header) -> --vh-offset. Measured
       from the hero's absolute Y so it's correct regardless of scroll position. */
    setHeaderOffset() {
      var rect = this.getBoundingClientRect();
      var top = rect.top + (window.pageYOffset || window.scrollY || 0);
      // Only the space that's actually above the fold matters; clamp sanely.
      var offset = Math.max(0, Math.min(Math.round(top), Math.round(window.innerHeight * 0.5)));
      this.style.setProperty('--vh-offset', offset + 'px');
    }

    handleBreakpointChange() {
      var isDesktop = this.desktopMedia.matches;
      if (isDesktop === this._lastDesktop) return; // width changed but not the breakpoint
      this._lastDesktop = isDesktop;

      // Re-point the active video at the matching orientation, preserving
      // roughly where we were. Non-active videos will swap when next shown.
      var slide = this.slides[this.index];
      var video = slide && slide._video;
      if (!video || this.reduced) return;

      var src = this.sourceFor(slide);
      if (src.url && video._loadedUrl !== src.url) {
        var wasTime = video.currentTime || 0;
        video._loadedUrl = src.url;
        video.classList.remove('is-failed', 'is-playing');
        video.src = src.url;
        video.load();
        video.addEventListener('loadedmetadata', function once() {
          try { video.currentTime = wasTime; } catch (e) {}
          video.removeEventListener('loadedmetadata', once);
        });
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }

    handleReducedChange() {
      if (this.reduced) {
        // Switch to poster-only mode.
        this.stopTimer();
        this.slides.forEach(this.pauseVideo, this);
      } else {
        this.activate(this.index, false);
      }
    }
  }

  /* ---- helpers -------------------------------------------------------- */
  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function addMediaListener(mql, handler) {
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else if (mql.addListener) mql.addListener(handler); // Safari < 14
  }

  if ('customElements' in window && !customElements.get('video-hero')) {
    customElements.define('video-hero', VideoHero);
  }
})();
